// QR Check-in system with rotating tokens
import { nanoid } from 'nanoid';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import type { Event } from '../types';
import { getEvent, updateEvent } from './firestore/events';
import { getRSVP, updateRSVP } from './firestore/events';
import QRCode from 'qrcode';

/**
 * Generate a rotating QR token for event check-in
 * Tokens rotate every N seconds (configurable per event)
 */
export const generateCheckInToken = async (
  eventId: string
): Promise<{ token: string; expiresAt: Date }> => {
  const event = await getEvent(eventId);
  if (!event) {
    throw new Error('Event not found');
  }

  // Default rotation: 60 seconds
  const rotateSeconds = event.qrMode?.rotateSeconds || 60;
  const token = nanoid(16); // Longer token for security
  const expiresAt = new Date(Date.now() + rotateSeconds * 1000);

  // Store token in event document (latest token)
  const eventRef = doc(db, 'events', eventId);
  
  await updateDoc(eventRef, {
    'qrMode.currentToken': token,
    'qrMode.tokenExpiresAt': Timestamp.fromDate(expiresAt),
    'qrMode.rotateSeconds': rotateSeconds,
  });

  return { token, expiresAt };
};

/**
 * Get current check-in token for an event
 */
export const getCurrentCheckInToken = async (
  eventId: string
): Promise<{ token: string; expiresAt: Date } | null> => {
  const event = await getEvent(eventId);
  if (!event || !event.qrMode) {
    return null;
  }

  const qrMode = event.qrMode as any;
  if (!qrMode.currentToken || !qrMode.tokenExpiresAt) {
    return null;
  }

  const expiresAt = qrMode.tokenExpiresAt instanceof Date
    ? qrMode.tokenExpiresAt
    : new Date(qrMode.tokenExpiresAt);

  // Check if expired
  if (expiresAt < new Date()) {
    // Generate new token
    return await generateCheckInToken(eventId);
  }

  return {
    token: qrMode.currentToken,
    expiresAt,
  };
};

/**
 * Verify and process check-in
 */
export const processCheckIn = async (
  eventId: string,
  token: string,
  userId: string,
  plusOnes: number = 0
): Promise<{ success: boolean; message: string }> => {
  try {
    const event = await getEvent(eventId);
    if (!event) {
      return { success: false, message: 'Event not found' };
    }

    // Verify token
    const qrMode = event.qrMode as any;
    if (!qrMode?.currentToken || qrMode.currentToken !== token) {
      return { success: false, message: 'Invalid or expired check-in token' };
    }

    const expiresAt = qrMode.tokenExpiresAt instanceof Date
      ? qrMode.tokenExpiresAt
      : new Date(qrMode.tokenExpiresAt);

    if (expiresAt < new Date()) {
      return { success: false, message: 'Check-in token has expired' };
    }

    // Get or create RSVP
    let rsvp = await getRSVP(eventId, userId);

    if (!rsvp || rsvp.state !== 'going') {
      return {
        success: false,
        message: 'You must RSVP as "Going" before checking in',
      };
    }

    // Update RSVP to checked_in
    await updateRSVP(eventId, userId, {
      state: 'checked_in',
      checkedInAt: new Date(),
      checkInMethod: 'qr',
      plusOnes: plusOnes || rsvp.plusOnes || 0,
    });

    // Update missed connections (background operation, don't wait for it)
    import('../lib/firestore/friends').then(({ updateMissedConnections }) => {
      updateMissedConnections(eventId, userId).catch((err) => {
        console.error('Failed to update missed connections:', err);
      });
    });

    return {
      success: true,
      message: `Checked in successfully${plusOnes > 0 ? ` with ${plusOnes} guest${plusOnes > 1 ? 's' : ''}` : ''}!`,
    };
  } catch (error: any) {
    return { success: false, message: error.message || 'Check-in failed' };
  }
};

/**
 * Generate QR code image data URL
 */
export const generateQRCode = async (url: string): Promise<string> => {
  try {
    const dataURL = await QRCode.toDataURL(url, {
      errorCorrectionLevel: 'M',
      type: 'image/png',
      width: 300,
      margin: 2,
    });
    return dataURL;
  } catch (error) {
    throw new Error('Failed to generate QR code');
  }
};

/**
 * Generate check-in URL for an event
 */
export const generateCheckInURL = (eventId: string, token: string): string => {
  const baseURL = window.location.origin;
  return `${baseURL}/checkin/${eventId}?token=${token}`;
};

